export type CellPosition = {
  rowId: string
  columnId: string
}

export type CellGrid = {
  rowIds: string[]
  columnIds: string[]
}

export type CellRange = {
  anchor: CellPosition
  focus: CellPosition
}

export type CellRangeBounds = {
  startRowIndex: number
  endRowIndex: number
  startColumnIndex: number
  endColumnIndex: number
}

export type MoveDirection = 'up' | 'down' | 'left' | 'right'
export type WrapDirection = 'next' | 'prev'

export function getCellKey(cell: CellPosition): string {
  return `${cell.rowId}\u0000${cell.columnId}`
}

export function areSameCell(
  left: CellPosition | null | undefined,
  right: CellPosition | null | undefined,
): boolean {
  return Boolean(
    left &&
    right &&
    left.rowId === right.rowId &&
    left.columnId === right.columnId,
  )
}

export function findCellIndex(
  grid: CellGrid,
  cell: CellPosition,
): { rowIndex: number; columnIndex: number } | null {
  const rowIndex = grid.rowIds.indexOf(cell.rowId)
  if (rowIndex === -1) return null

  const columnIndex = grid.columnIds.indexOf(cell.columnId)
  if (columnIndex === -1) return null

  return { rowIndex, columnIndex }
}

export function getCellAt(
  grid: CellGrid,
  rowIndex: number,
  columnIndex: number,
): CellPosition | null {
  const rowId = grid.rowIds[rowIndex]
  const columnId = grid.columnIds[columnIndex]
  if (!rowId || !columnId) return null
  return { rowId, columnId }
}

export function moveCell(
  grid: CellGrid,
  current: CellPosition,
  direction: MoveDirection,
): CellPosition | null {
  const index = findCellIndex(grid, current)
  if (!index) return null

  let nextRowIndex = index.rowIndex
  let nextColumnIndex = index.columnIndex

  if (direction === 'up') {
    nextRowIndex = Math.max(0, index.rowIndex - 1)
  } else if (direction === 'down') {
    nextRowIndex = Math.min(grid.rowIds.length - 1, index.rowIndex + 1)
  } else if (direction === 'left') {
    nextColumnIndex = Math.max(0, index.columnIndex - 1)
  } else {
    nextColumnIndex = Math.min(grid.columnIds.length - 1, index.columnIndex + 1)
  }

  return getCellAt(grid, nextRowIndex, nextColumnIndex)
}

export function moveCellWithWrap(
  grid: CellGrid,
  current: CellPosition,
  direction: WrapDirection,
): CellPosition | null {
  const index = findCellIndex(grid, current)
  if (!index) return null

  let nextRowIndex = index.rowIndex
  let nextColumnIndex = index.columnIndex

  if (direction === 'next') {
    nextColumnIndex += 1
    if (nextColumnIndex >= grid.columnIds.length) {
      nextRowIndex += 1
      nextColumnIndex = 0
    }
  } else {
    nextColumnIndex -= 1
    if (nextColumnIndex < 0) {
      nextRowIndex -= 1
      nextColumnIndex = grid.columnIds.length - 1
    }
  }

  if (
    nextRowIndex < 0 ||
    nextRowIndex >= grid.rowIds.length ||
    nextColumnIndex < 0 ||
    nextColumnIndex >= grid.columnIds.length
  ) {
    return null
  }

  return getCellAt(grid, nextRowIndex, nextColumnIndex)
}

export function getCellRangeBounds(
  grid: CellGrid,
  range: CellRange,
): CellRangeBounds | null {
  const anchor = findCellIndex(grid, range.anchor)
  const focus = findCellIndex(grid, range.focus)
  if (!anchor || !focus) return null

  return {
    startRowIndex: Math.min(anchor.rowIndex, focus.rowIndex),
    endRowIndex: Math.max(anchor.rowIndex, focus.rowIndex),
    startColumnIndex: Math.min(anchor.columnIndex, focus.columnIndex),
    endColumnIndex: Math.max(anchor.columnIndex, focus.columnIndex),
  }
}

export function isCellInRange(
  grid: CellGrid,
  range: CellRange | null,
  cell: CellPosition,
): boolean {
  if (!range) return false

  const bounds = getCellRangeBounds(grid, range)
  const index = findCellIndex(grid, cell)
  if (!bounds || !index) return false

  return (
    index.rowIndex >= bounds.startRowIndex &&
    index.rowIndex <= bounds.endRowIndex &&
    index.columnIndex >= bounds.startColumnIndex &&
    index.columnIndex <= bounds.endColumnIndex
  )
}

export function getCellsInRange(
  grid: CellGrid,
  range: CellRange,
): CellPosition[] {
  const bounds = getCellRangeBounds(grid, range)
  if (!bounds) return []

  const cells: CellPosition[] = []
  for (
    let rowIndex = bounds.startRowIndex;
    rowIndex <= bounds.endRowIndex;
    rowIndex += 1
  ) {
    for (
      let columnIndex = bounds.startColumnIndex;
      columnIndex <= bounds.endColumnIndex;
      columnIndex += 1
    ) {
      const cell = getCellAt(grid, rowIndex, columnIndex)
      if (cell) cells.push(cell)
    }
  }

  return cells
}

export function parseClipboardMatrix(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const rows = normalized.split('\n')

  if (rows.length > 1 && rows[rows.length - 1] === '') {
    rows.pop()
  }

  return rows.map(row => row.split('\t'))
}

export type CellPasteTarget = CellPosition & {
  text: string
}

export function getPasteTargets({
  grid,
  start,
  matrix,
  selection,
}: {
  grid: CellGrid
  start: CellPosition
  matrix: string[][]
  selection?: CellRange | null
}): CellPasteTarget[] {
  if (matrix.length === 0 || matrix[0]?.length === 0) return []

  const isSingleValue = matrix.length === 1 && matrix[0].length === 1
  if (isSingleValue && selection) {
    return getCellsInRange(grid, selection).map(cell => ({
      ...cell,
      text: matrix[0][0],
    }))
  }

  const startIndex = findCellIndex(grid, start)
  if (!startIndex) return []

  const targets: CellPasteTarget[] = []
  matrix.forEach((row, rowOffset) => {
    row.forEach((text, columnOffset) => {
      const cell = getCellAt(
        grid,
        startIndex.rowIndex + rowOffset,
        startIndex.columnIndex + columnOffset,
      )
      if (cell) {
        targets.push({ ...cell, text })
      }
    })
  })

  return targets
}

export function getFillDownTargets({
  grid,
  source,
  target,
  getValue,
}: {
  grid: CellGrid
  source: CellRange
  target: CellPosition
  getValue: (cell: CellPosition) => string
}): CellPasteTarget[] {
  const sourceBounds = getCellRangeBounds(grid, source)
  const targetIndex = findCellIndex(grid, target)
  if (!sourceBounds || !targetIndex) return []
  if (targetIndex.rowIndex <= sourceBounds.endRowIndex) return []

  const sourceRowCount =
    sourceBounds.endRowIndex - sourceBounds.startRowIndex + 1
  const targets: CellPasteTarget[] = []

  for (
    let rowIndex = sourceBounds.endRowIndex + 1;
    rowIndex <= targetIndex.rowIndex;
    rowIndex += 1
  ) {
    const sourceRowIndex =
      sourceBounds.startRowIndex +
      ((rowIndex - sourceBounds.endRowIndex - 1) % sourceRowCount)

    for (
      let columnIndex = sourceBounds.startColumnIndex;
      columnIndex <= sourceBounds.endColumnIndex;
      columnIndex += 1
    ) {
      const cell = getCellAt(grid, rowIndex, columnIndex)
      const sourceCell = getCellAt(grid, sourceRowIndex, columnIndex)
      if (cell && sourceCell) {
        targets.push({ ...cell, text: getValue(sourceCell) })
      }
    }
  }

  return targets
}
